import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Sun, Moon, RefreshCw, ShieldCheck, 
  Database, Zap, CheckCircle2, LayoutDashboard,
  Users, Building2, BookCopy, ArrowLeft, Menu, X,
  Activity, HelpCircle, Key, Info, Brain, Sparkles,
  Fingerprint, TrendingUp, Monitor, Cpu, Globe, Lock, MessageSquare
} from 'lucide-react';
import './DocsPage.css';

export default function DocsPage() {
    const [theme, setTheme] = useState('dark');
    const [activeSection, setActiveSection] = useState('welcome');
    const [menuOpen, setMenuOpen] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        if (theme === 'dark') {
            document.documentElement.classList.add('dark');
        } else {
            document.documentElement.classList.remove('dark');
        }
    }, [theme]);

    useEffect(() => {
        const handleScroll = () => {
            const sections = document.querySelectorAll('.docs-section');
            let current = 'welcome';
            sections.forEach(section => {
                const sectionTop = section.offsetTop;
                if (window.pageYOffset >= sectionTop - 150) {
                    current = section.getAttribute('id');
                }
            });
            setActiveSection(current);

            const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
            const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrolled = (winScroll / height) * 100;
            setScrollProgress(scrolled);
        };

        // Scroll to hash on mount
        if (window.location.hash) {
            const id = window.location.hash.substring(1);
            setTimeout(() => {
                const element = document.getElementById(id);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }, 500); // Give time for content to render
        }

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => prev === 'dark' ? 'light' : 'dark');
    };

    const scrollTo = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setMenuOpen(false);
        }
    };

    return (
        <div className="docs-wrapper">
            <header className="docs-header">
                <div className="flex items-center gap-2">
                    <button 
                        className="mobile-menu-btn" 
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle Menu"
                    >
                        {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                    <Link to="/" className="docs-logo">
                        <img src={theme === 'dark' ? '/logo.png' : '/logo_black.png'} alt="UIAF" className="w-8 h-8 object-contain" />
                        <span className="font-black tracking-tighter uppercase text-sm sm:text-lg">Documentation</span>
                    </Link>
                </div>
                <div className="flex items-center gap-4">
                    <Link to="/" className="text-sm font-bold hover:text-blue-500 transition-colors flex items-center gap-2">
                        <ArrowLeft className="w-4 h-4" /> <span className="hidden xs:inline uppercase tracking-widest text-[10px]">Back to App</span>
                    </Link>
                    <button className="docs-theme-toggle" onClick={toggleTheme}>
                        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                    </button>
                </div>
            </header>

            <div className="docs-progress-container">
                <div 
                    className="docs-progress-bar" 
                    style={{ width: `${scrollProgress}%` }}
                ></div>
            </div>

            <div className="docs-container">
                <aside className={`docs-aside ${menuOpen ? 'open' : ''}`}>
                    <div className="docs-nav-section">
                        <h3 className="docs-nav-section-title">Getting Started</h3>
                        <button onClick={() => scrollTo('welcome')} className={`docs-nav-link ${activeSection === 'welcome' ? 'active' : ''}`}><Info className="w-4 h-4"/> Welcome</button>
                        <button onClick={() => scrollTo('quick-start')} className={`docs-nav-link ${activeSection === 'quick-start' ? 'active' : ''}`}><Zap className="w-4 h-4"/> 5-Step Setup</button>
                    </div>
                    <div className="docs-nav-section">
                        <h3 className="docs-nav-section-title">Admin Core</h3>
                        <button onClick={() => scrollTo('dashboard')} className={`docs-nav-link ${activeSection === 'dashboard' ? 'active' : ''}`}><LayoutDashboard className="w-4 h-4"/> Dashboard</button>
                        <button onClick={() => scrollTo('resources')} className={`docs-nav-link ${activeSection === 'resources' ? 'active' : ''}`}><Database className="w-4 h-4"/> Resources</button>
                        <button onClick={() => scrollTo('assignments')} className={`docs-nav-link ${activeSection === 'assignments' ? 'active' : ''}`}><BookCopy className="w-4 h-4"/> Assignments</button>
                        <button onClick={() => scrollTo('calendar')} className={`docs-nav-link ${activeSection === 'calendar' ? 'active' : ''}`}><Calendar className="w-4 h-4"/> Scheduler</button>
                    </div>
                    <div className="docs-nav-section">
                        <h3 className="docs-nav-section-title">Enterprise Modules</h3>
                        <button onClick={() => scrollTo('attendance')} className={`docs-nav-link ${activeSection === 'attendance' ? 'active' : ''}`}><Fingerprint className="w-4 h-4"/> Attendance</button>
                        <button onClick={() => scrollTo('performance')} className={`docs-nav-link ${activeSection === 'performance' ? 'active' : ''}`}><TrendingUp className="w-4 h-4"/> Performance</button>
                        <button onClick={() => scrollTo('inquiries')} className={`docs-nav-link ${activeSection === 'inquiries' ? 'active' : ''}`}><MessageSquare className="w-4 h-4"/> Inquiries</button>
                        <button onClick={() => scrollTo('monitoring')} className={`docs-nav-link ${activeSection === 'monitoring' ? 'active' : ''}`}><Activity className="w-4 h-4"/> Activity Monitor</button>
                    </div>
                    <div className="docs-nav-section">
                        <h3 className="docs-nav-section-title">Intelligence</h3>
                        <button onClick={() => scrollTo('benchrex-portal')} className={`docs-nav-link ${activeSection === 'benchrex-portal' ? 'active' : ''}`}><Sparkles className="w-4 h-4 text-amber-500"/> Benchrex AI Portal</button>
                        <button onClick={() => scrollTo('user-portal')} className={`docs-nav-link ${activeSection === 'user-portal' ? 'active' : ''}`}><Monitor className="w-4 h-4"/> User Experience</button>
                        <button onClick={() => scrollTo('auto-sync')} className={`docs-nav-link ${activeSection === 'auto-sync' ? 'active' : ''}`}><RefreshCw className="w-4 h-4"/> Google Sync</button>
                    </div>
                </aside>

                <main className="docs-main">
                    <section id="welcome" className="docs-section">
                        <div className="docs-badge">UIAF Platform Guide</div>
                        <h1 className="docs-h1">Unified Intelligent Admin Forum</h1>
                        <p className="docs-subtitle">Welcome to UIAF OS—the industry's first centralized ecosystem for intelligent scheduling, persona-driven AI, and total data sovereignty.</p>
                        
                        <div className="docs-grid">
                            <div className="docs-card">
                                <div className="docs-card-icon text-blue-500"><Globe className="w-5 h-5" /></div>
                                <div className="docs-card-title">Centralized Ecosystem</div>
                                <div className="docs-card-desc">One platform to rule your entire institution, from scheduling to performance analytics.</div>
                            </div>
                            <div className="docs-card">
                                <div className="docs-card-icon text-amber-500"><Sparkles className="w-5 h-5" /></div>
                                <div className="docs-card-title">Persona-Driven AI</div>
                                <div className="docs-card-desc">Specialized AI assistants tailored for Faculty, Students, and Administrators.</div>
                            </div>
                            <div className="docs-card">
                                <div className="docs-card-icon text-emerald-500"><Lock className="w-5 h-5" /></div>
                                <div className="docs-card-title">Data Sovereignty</div>
                                <div className="docs-card-desc">Your data stays yours. Enterprise-grade isolation and multi-tenant security.</div>
                            </div>
                        </div>
                    </section>

                    <section id="quick-start" className="docs-section">
                        <h2 className="docs-h2"><Zap className="w-6 h-6 text-yellow-500" /> 5-Step Operational Launch</h2>
                        <p className="docs-p">Initialize your UIAF OS environment in 5 strategic moves:</p>
                        <div className="docs-prose">
                            <ol className="space-y-4">
                                <li><strong>Infrastructure Setup:</strong> Define Branches and Rooms in the Resources panel.</li>
                                <li><strong>Human Capital:</strong> Invite Faculty members and configure their working hour preferences.</li>
                                <li><strong>Knowledge Mapping:</strong> Build your Courses and Subjects in the Knowledge Bank.</li>
                                <li><strong>Audience Segmentation:</strong> Group students into Batches for targeted scheduling and AI access.</li>
                                <li><strong>Automated Execution:</strong> Assign Faculty to Subjects and let the engine find the optimal timetable.</li>
                            </ol>
                        </div>
                    </section>

                    <section id="benchrex-portal" className="docs-section gold-section">
                        <div className="gold-badge"><Sparkles className="w-3 h-3" /> Premium Feature</div>
                        <h2 className="docs-h2 gold-text"><Cpu className="w-7 h-7" /> Benchrex AI Portal</h2>
                        <p className="docs-p" style={{ color: 'var(--docs-foreground)' }}>The Benchrex AI Portal is the crown jewel of UIAF OS. It is a high-performance intelligence layer that processes institutional knowledge and serves it through specialized AI personas.</p>
                        
                        <div className="docs-table-wrapper">
                            <table className="docs-table">
                                <thead>
                                    <tr>
                                        <th>Intelligence Module</th>
                                        <th>Capability</th>
                                        <th>Strategic Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Persona Governance</strong></td>
                                        <td>Deploy custom AI assistants (e.g., Academic Advisor, HR Bot).</td>
                                        <td>Ensures all AI interactions follow institutional protocols and tone.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Knowledge Banking</strong></td>
                                        <td>Process PDFs, CSVs, and web data into AI-readable formats.</td>
                                        <td>Turns static documents into an interactive, 24/7 knowledge base.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Expert Handover</strong></td>
                                        <td>Seamlessly escalate complex AI queries to human administrators.</td>
                                        <td>Maintains high student satisfaction with a human-in-the-loop fallback.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>RAG-Powered Search</strong></td>
                                        <td>Instant, citation-backed answers from your own data vault.</td>
                                        <td>Eliminates "hallucinations" by grounding AI in actual institutional facts.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="dashboard" className="docs-section">
                        <h2 className="docs-h2"><LayoutDashboard className="w-6 h-6 text-blue-500" /> Dashboard: Institutional Pulse</h2>
                        <p className="docs-p">The Dashboard provides 360° visibility into academic performance, resource load, and system health.</p>
                        
                        <div className="docs-table-wrapper">
                            <table className="docs-table">
                                <thead>
                                    <tr>
                                        <th>Metric</th>
                                        <th>Analysis Provided</th>
                                        <th>Operational Move</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Resource Utilization</strong></td>
                                        <td>Real-time occupancy of rooms across all branches.</td>
                                        <td>Identify underused spaces to optimize facility costs.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Faculty Allotment</strong></td>
                                        <td>Distribution of teaching hours vs. contractual limits.</td>
                                        <td>Prevent educator burnout by balancing weekly loads.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Job Pipeline</strong></td>
                                        <td>Status of active scheduling and AI processing tasks.</td>
                                        <td>Monitor system throughput during heavy planning periods.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="attendance" className="docs-section">
                        <h2 className="docs-h2"><Fingerprint className="w-6 h-6 text-cyan-500" /> Attendance Management</h2>
                        <p className="docs-p">Our automated attendance ecosystem eliminates manual paperwork and provides tamper-proof tracking.</p>
                        
                        <div className="docs-table-wrapper">
                            <table className="docs-table">
                                <thead>
                                    <tr>
                                        <th>Feature</th>
                                        <th>Security/Method</th>
                                        <th>Result</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Smart QR Marking</strong></td>
                                        <td>Time-sensitive, dynamic QR codes displayed in class.</td>
                                        <td>Prevents "proxy" attendance marking by students.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Geo-Fencing</strong></td>
                                        <td>Verifies student location within the branch perimeter.</td>
                                        <td>Ensures students are physically present in the room.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Auto-Alerts</strong></td>
                                        <td>Triggers emails/SMS for consecutive absences.</td>
                                        <td>Instant notification to parents and administrators.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="performance" className="docs-section">
                        <h2 className="docs-h2"><TrendingUp className="w-6 h-6 text-emerald-500" /> Performance Monitoring</h2>
                        <p className="docs-p">Track student success and institutional growth through data-driven performance metrics.</p>
                        
                        <div className="docs-table-wrapper">
                            <table className="docs-table">
                                <thead>
                                    <tr>
                                        <th>Metric</th>
                                        <th>Data Point</th>
                                        <th>Outcome</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Risk Detection</strong></td>
                                        <td>Combined attendance and inquiry engagement scores.</td>
                                        <td>Early detection of students likely to drop out or fail.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Curriculum Health</strong></td>
                                        <td>Coverage of subjects vs. planned academic timeline.</td>
                                        <td>Ensures syllabus is completed on schedule across all batches.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Engagement Score</strong></td>
                                        <td>Frequency and depth of interactions with AI personas.</td>
                                        <td>Measures student curiosity and active learning patterns.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="inquiries" className="docs-section">
                        <h2 className="docs-h2"><MessageSquare className="w-6 h-6 text-purple-500" /> Inquiry Management</h2>
                        <p className="docs-p">Manage and resolve student/faculty doubts through a unified communication hub.</p>
                        
                        <div className="docs-table-wrapper">
                            <table className="docs-table">
                                <thead>
                                    <tr>
                                        <th>Workflow</th>
                                        <th>Process</th>
                                        <th>User Benefit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>AI-First Triage</strong></td>
                                        <td>Common queries are automatically handled by Benchrex AI.</td>
                                        <td>Instant resolution for routine questions without staff intervention.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Ticket Escalation</strong></td>
                                        <td>Complex queries are converted into tickets for human experts.</td>
                                        <td>Ensures no query is left unanswered, maintaining trust.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Knowledge Expansion</strong></td>
                                        <td>Successful resolutions can be added back to the Content Bank.</td>
                                        <td>The system gets smarter with every interaction.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="user-portal" className="docs-section">
                        <h2 className="docs-h2"><Monitor className="w-6 h-6 text-zinc-400" /> The User Experience</h2>
                        <p className="docs-p">UIAF OS isn't just for admins. The User Portal empowers faculty and students with personalized tools.</p>
                        
                        <div className="docs-table-wrapper">
                            <table className="docs-table">
                                <thead>
                                    <tr>
                                        <th>User Type</th>
                                        <th>Key Feature</th>
                                        <th>Effective Usage Tip</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><strong>Faculty</strong></td>
                                        <td><strong>Personal Schedule Sync</strong></td>
                                        <td>Connect your Google Calendar to auto-block personal time.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Student</strong></td>
                                        <td><strong>AI Tutor (Benchrex)</strong></td>
                                        <td>Ask about specific subjects to get RAG-backed explanations.</td>
                                    </tr>
                                    <tr>
                                        <td><strong>Student</strong></td>
                                        <td><strong>Digital ID Card</strong></td>
                                        <td>Use the QR Scanner in the portal for instant class check-ins.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="auto-sync" className="docs-section">
                        <h2 className="docs-h2"><RefreshCw className="w-6 h-6 text-blue-500" /> Enterprise Integration</h2>
                        <p className="docs-p">UIAF OS integrates seamlessly with Google Workspace, Microsoft 365, and world-class AI models from OpenAI, Anthropic, and Google Gemini.</p>
                    </section>

                    <footer className="docs-footer">
                        <div className="flex items-center gap-2 mb-4 sm:mb-0">
                            <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white font-black text-[10px]">U</div>
                            <span className="font-black tracking-tighter uppercase text-sm">&copy; 2024 UIAF OS Infrastructure</span>
                        </div>
                        <div className="flex gap-6 text-[11px] font-bold uppercase tracking-widest text-zinc-500">
                            <Link to="/privacy" className="hover:text-blue-500 transition-colors">Privacy</Link>
                            <Link to="/license" className="hover:text-blue-500 transition-colors">License</Link>
                            <a href="mailto:ops@uiaf.com" className="hover:text-blue-500 transition-colors">Operations</a>
                        </div>
                    </footer>
                </main>
            </div>
        </div>
    );
}
